// classifyStageNodes — Groups classified events by journey stage, determines
// each stage's visual state, computes badges, detects alerts, and filters
// empty stages.
//
// Pure function: no React dependencies, no side effects.
//
// Validates: Requirements 2.1, 2.3, 2.5, 2.6, 2.7, 4.1, 4.2, 4.3, 4.4, 4.5

import {
  JOURNEY_STAGE_ORDER,
  ALERT_TYPE_MAP,
  createStageBadge,
  createStageNodeData,
} from '../../types/tripcard';

/**
 * @typedef {import('../../types/tripcard').JourneyStage} JourneyStage
 * @typedef {import('../../types/tripcard').StageNodeData} StageNodeData
 * @typedef {import('../../types/tripcard').AlertInfo} AlertInfo
 * @typedef {import('../../types/tripcard').StageBadge} StageBadge
 */

// ── Helpers ─────────────────────────────────────────────────

/**
 * Finds the chronologically nearest stage for an unclassifiable event.
 * Compares the event's scheduledStart against the earliest event in each
 * populated stage bucket, returning the stage with the smallest time gap.
 *
 * @param {Object} event - The unclassifiable event
 * @param {Map<string, Array<Object>>} stageBuckets - Map of stage → events already assigned
 * @returns {JourneyStage} The nearest stage to assign the event to
 */
function findNearestStage(event, stageBuckets) {
  const eventTime = event.scheduledStart instanceof Date
    ? event.scheduledStart.getTime()
    : new Date(event.scheduledStart).getTime();

  let nearestStage = null;
  let smallestGap = Infinity;

  for (const [stage, bucket] of stageBuckets) {
    if (!bucket || bucket.length === 0) continue;

    for (const existing of bucket) {
      const existingTime = existing.scheduledStart instanceof Date
        ? existing.scheduledStart.getTime()
        : new Date(existing.scheduledStart).getTime();
      const gap = Math.abs(eventTime - existingTime);
      if (gap < smallestGap) {
        smallestGap = gap;
        nearestStage = stage;
      }
    }
  }

  // If no stages have events yet, fall back to the first stage in canonical order
  return nearestStage || JOURNEY_STAGE_ORDER[0];
}

/**
 * Determines the most severe alert from a list of events.
 * Critical (cancellation) takes priority over warning (delay, gate_change).
 *
 * @param {Array<Object>} events - Events to scan for alerts
 * @returns {AlertInfo|undefined}
 */
function detectStageAlert(events) {
  let mostSevere = undefined;

  for (const event of events) {
    if (!event.alert) continue;

    if (!mostSevere) {
      mostSevere = event.alert;
      continue;
    }

    // Critical always wins over warning
    if (event.alert.type === 'critical' && mostSevere.type !== 'critical') {
      mostSevere = event.alert;
    }
  }

  return mostSevere;
}

/**
 * Computes the relative time string for a stage badge.
 *
 * - Active stage → "now"
 * - ≥1 hour away → "in Xh Ym"
 * - <1 hour away → "in X min"
 *
 * @param {string} stageState - 'completed' | 'active' | 'upcoming'
 * @param {Array<Object>} events - Events in this stage
 * @param {Date} now - Current time
 * @returns {string}
 */
function computeRelativeTime(stageState, events, now) {
  if (stageState === 'active') return 'now';
  if (stageState === 'completed') return '';

  // For upcoming stages, find the earliest event start time
  const nowMs = now.getTime();
  let earliestMs = Infinity;

  for (const event of events) {
    const startMs = event.scheduledStart instanceof Date
      ? event.scheduledStart.getTime()
      : new Date(event.scheduledStart).getTime();
    if (startMs < earliestMs) {
      earliestMs = startMs;
    }
  }

  if (earliestMs === Infinity || earliestMs <= nowMs) return '';

  const diffMs = earliestMs - nowMs;
  const diffMinutes = Math.round(diffMs / 60000);

  if (diffMinutes >= 60) {
    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;
    return minutes > 0 ? `in ${hours}h ${minutes}m` : `in ${hours}h 0m`;
  }

  return `in ${diffMinutes} min`;
}

/**
 * Determines the stage state based on its position relative to the active stage
 * in the canonical order.
 *
 * @param {JourneyStage} stage - The stage to classify
 * @param {string} activeStageId - The currently active stage ID
 * @param {Array<string>} stageOrder - The stage order to use for index lookup
 * @returns {'completed' | 'active' | 'upcoming'}
 */
function resolveStageState(stage, activeStageId, stageOrder) {
  if (!activeStageId) return 'upcoming';

  const stageIndex = stageOrder.indexOf(stage);
  const activeIndex = stageOrder.indexOf(activeStageId);

  if (activeIndex === -1) return 'upcoming';
  if (stageIndex < activeIndex) return 'completed';
  if (stageIndex === activeIndex) return 'active';
  return 'upcoming';
}

// ── Main ────────────────────────────────────────────────────

/**
 * Groups classified events by journey stage, determines each stage's visual
 * state, computes badges, detects alerts, and filters empty stages.
 *
 * Each event is assigned to exactly one stage. Events with a valid `parentStage`
 * go to that stage. Unclassifiable events (no parentStage or parentStage not in
 * the canonical eight) go to the chronologically nearest stage.
 *
 * Returns stages in the canonical eight-stage order, omitting stages with zero events.
 *
 * @param {Array<Object>} events - ClassifiedEvent objects from buildJourneyEvents
 * @param {string} activeStageId - The currently active stage (e.g. 'in_flight')
 * @param {Date} now - Current time for badge computation
 * @param {Object} [options] - Optional configuration
 * @param {Array<string>} [options.stageOrder] - Custom stage order (defaults to JOURNEY_STAGE_ORDER)
 * @returns {Array<StageNodeData>}
 */
export function classifyStageNodes(events, activeStageId, now, options = {}) {
  const stageOrder = options.stageOrder || JOURNEY_STAGE_ORDER;
  const safeNow = now instanceof Date && !Number.isNaN(now.getTime())
    ? now
    : new Date();

  const safeEvents = Array.isArray(events) ? events : [];

  // Step 1: Create buckets for each canonical stage
  /** @type {Map<string, Array<Object>>} */
  const stageBuckets = new Map();
  for (const stage of stageOrder) {
    stageBuckets.set(stage, []);
  }

  // Step 2: First pass — assign events with valid parentStage
  const unclassified = [];

  for (const event of safeEvents) {
    const parentStage = event.parentStage;

    if (parentStage && stageOrder.includes(parentStage)) {
      stageBuckets.get(parentStage).push(event);
    } else {
      unclassified.push(event);
    }
  }

  // Step 3: Second pass — assign unclassifiable events to nearest stage
  for (const event of unclassified) {
    const nearestStage = findNearestStage(event, stageBuckets);
    stageBuckets.get(nearestStage).push(event);
  }

  // Step 4: Build StageNodeData for each non-empty stage in canonical order
  /** @type {Array<StageNodeData>} */
  const result = [];

  for (const stage of stageOrder) {
    const stageEvents = stageBuckets.get(stage);
    if (stageEvents.length === 0) continue;

    const stageState = resolveStageState(stage, activeStageId, stageOrder);
    const alert = detectStageAlert(stageEvents);
    const relativeTime = computeRelativeTime(stageState, stageEvents, safeNow);

    /** @type {StageBadge} */
    const badge = createStageBadge(stageEvents.length);
    badge.relativeTime = relativeTime;
    badge.hasAlert = !!alert;
    badge.alertType = alert ? alert.type : undefined;

    /** @type {StageNodeData} */
    const node = createStageNodeData(
      `stage:${stage}`,
      stage,
      stageState,
    );
    node.events = stageEvents;
    node.badge = badge;
    node.alert = alert;
    node.isVisible = true;

    result.push(node);
  }

  return result;
}

export default classifyStageNodes;
